import {
  AdminAuthResponse,
  AdminOrgListItem,
  AuditLog,
  InviteRecord,
  PlatformAdmin,
  PlatformAdminListItem,
} from '../types';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8787';
const TOKEN_KEY = 'admin_token';

class AdminApiService {
  private token: string | null = null;

  constructor() {
    this.token = localStorage.getItem(TOKEN_KEY);
  }

  setToken(token: string) {
    this.token = token;
    localStorage.setItem(TOKEN_KEY, token);
  }

  clearToken() {
    this.token = null;
    localStorage.removeItem(TOKEN_KEY);
  }

  hasToken(): boolean {
    return !!this.token;
  }

  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }

    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      headers,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Request failed' }));
      throw new Error(error.error || `HTTP ${response.status}`);
    }

    return response.json();
  }

  // ── Auth ────────────────────────────────────────────────────────────

  async login(email: string, password: string): Promise<AdminAuthResponse> {
    const result = await this.request<AdminAuthResponse>('/admin/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
    this.setToken(result.token);
    return result;
  }

  async getMe(): Promise<{ admin: PlatformAdmin }> {
    return this.request('/admin/auth/me');
  }

  // ── Platform admin management ───────────────────────────────────────

  async listAdmins(): Promise<{ admins: PlatformAdminListItem[] }> {
    return this.request('/admin/admins');
  }

  async createAdmin(data: {
    email: string;
    fullName: string;
    password: string;
  }): Promise<{ admin: PlatformAdminListItem }> {
    return this.request('/admin/admins', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async deactivateAdmin(id: string): Promise<{ success: boolean }> {
    return this.request(`/admin/admins/${id}/deactivate`, { method: 'PATCH' });
  }

  async reactivateAdmin(id: string): Promise<{ success: boolean }> {
    return this.request(`/admin/admins/${id}/reactivate`, { method: 'PATCH' });
  }

  // ── Organizations ───────────────────────────────────────────────────

  async listOrganizations(): Promise<{ organizations: AdminOrgListItem[] }> {
    return this.request('/admin/organizations');
  }

  async createOrganization(data: {
    orgName: string;
    subdomain: string;
    ownerEmail: string;
    ownerFullName: string;
    ownerPassword: string;
  }): Promise<{
    organization: { id: string; subdomain: string; name: string };
    owner: { id: string; email: string; fullName: string; isNew: boolean };
  }> {
    return this.request('/admin/organizations', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async disableOrganization(id: string): Promise<{ success: boolean }> {
    return this.request(`/admin/organizations/${id}/disable`, { method: 'PATCH' });
  }

  async enableOrganization(id: string): Promise<{ success: boolean }> {
    return this.request(`/admin/organizations/${id}/enable`, { method: 'PATCH' });
  }

  async impersonateOrganization(id: string): Promise<{
    token: string;
    subdomain: string;
    ownerEmail: string;
  }> {
    return this.request(`/admin/organizations/${id}/impersonate`, { method: 'POST' });
  }

  // ── Invites ─────────────────────────────────────────────────────────

  async listInvites(): Promise<{ invites: InviteRecord[] }> {
    return this.request('/admin/invites');
  }

  async createInvite(data: {
    invite_type: 'new_org' | 'join_org';
    email?: string;
    organization_id?: string;
    role?: 'owner' | 'manager' | 'cashier';
    suggested_subdomain?: string;
    expires_in_days?: number;
  }): Promise<{ invite: InviteRecord; redemption_url: string }> {
    return this.request('/admin/invites', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async revokeInvite(id: string): Promise<{ success: boolean }> {
    return this.request(`/admin/invites/${id}`, { method: 'DELETE' });
  }

  // ── Audit logs ──────────────────────────────────────────────────────

  async listAuditLogs(params?: {
    limit?: number;
    offset?: number;
    resource_type?: string;
    resource_id?: string;
  }): Promise<{ logs: AuditLog[] }> {
    const qs = new URLSearchParams();
    if (params?.limit)         qs.set('limit', String(params.limit));
    if (params?.offset)        qs.set('offset', String(params.offset));
    if (params?.resource_type) qs.set('resource_type', params.resource_type);
    if (params?.resource_id)   qs.set('resource_id', params.resource_id);
    const query = qs.toString() ? `?${qs}` : '';
    return this.request(`/admin/audit-logs${query}`);
  }
}

export const adminApi = new AdminApiService();
